/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type VehicleSummary } from '@/types';
import cx from 'classnames';
import styles from './AltYMMEContentBlock.module.scss';
import Image from '@/components/NextImage';
import { useLabels } from '@/hooks/useLabels';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageType } from '@/hooks/usePageType';
import { pageTypes } from '@/constants/page';
import { Link, Text, View } from '@az/starc-ui';
import { getVehicleTypeIcon } from '../../utils/getVehicleTypeIcon';

type Props = {
  preferredVehicle: VehicleSummary;
  openAQYMME: () => void;
  setShowYmme: () => void;
};

const moreInfoNeededIconSrc = '/images/icon-fitment-more-info-needed.svg';

export const labelMap = {
  lblCurrentlyShoppingFor: 'label_CurrentlyShoppingFor',
  lblSubModel: 'label_SubModel',
  lblEngine: 'label_Engine',
  lblChangeVehicle: 'button_YMME_Body_ChangeVehicle',
  lblShopWithoutVehicle: 'button_YMME_Body_ShopWithoutVehicle',
  label_ymme_more_info_for_exact_fit: 'label_ymme_more_info_for_exact_fit',
  label_ymme_more_info_add_details: 'label_ymme_more_info_add_details',
};

const AddDetailsButton = ({
  openAQYMME,
  labels,
}: {
  openAQYMME: () => void;
  labels: Record<string, string>;
}) => (
  <button type="button" onClick={openAQYMME} className={styles.addDetailsButton}>
    <Text maxLines={1} className={styles.label}>
      {labels.label_ymme_more_info_add_details}
    </Text>
  </button>
);

export const MoreInfoNeededInlineYMME = ({ preferredVehicle, openAQYMME, setShowYmme }: Props) => {
  const labels = useLabels(labelMap);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isProductDetailsPageType =
    usePageType().pageType?.toLowerCase() === pageTypes.ProductDetail.toLowerCase();

  const vehicleIconSrc = getVehicleTypeIcon(preferredVehicle.vehicleTypeName, 'desktop', 'base');

  return (
    <View
      className={cx(styles.mainContainer, isProductDetailsPageType ? styles.center : undefined)}
    >
      <div className={styles.infoContainer}>
        <div className={styles.imgContainer}>
          <Image src={vehicleIconSrc} alt="vehicle-icon-base" width={32} height={23} />
          <div className={styles.imgSmall}>
            <Image src={moreInfoNeededIconSrc} alt="vehicle-icon-mark" width={13} height={13} />
          </div>
        </div>
        <div className={styles.labelsContainer}>
          <Text className={styles.title}>{labels.label_ymme_more_info_for_exact_fit}</Text>
          <View className={styles.subtitleContainer}>
            <div className={styles.labels}>
              <Text className={styles.carTitleText}>
                {preferredVehicle.year} {preferredVehicle.make} {preferredVehicle.model}
              </Text>
              <Link className={styles.changeVehicleButton} onClick={setShowYmme}>
                {labels.lblChangeVehicle}
              </Link>
            </div>
            {isMobile ? <AddDetailsButton openAQYMME={openAQYMME} labels={labels} /> : null}
          </View>
        </div>
      </div>
      {!isMobile ? <AddDetailsButton openAQYMME={openAQYMME} labels={labels} /> : null}
    </View>
  );
};
